import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
  type MRT_PaginationState,
} from "material-react-table";
import {
  Button,
  IconButton,
  MenuItem,
  Tooltip,
  Chip,
  Stack,
  Paper,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import MemoryIcon from "@mui/icons-material/Memory";
import Title from "../../../../../components/Title";
import { debounce } from "lodash";
import { fetchAgents } from "../../../../../features/slices/Agent";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../../store";
import { GlobalModal } from "../../../../../components/GlobalModal";
import { JsonEditor } from "json-edit-react";
import { useNavigate } from "react-router-dom";
import Back from "../../../../../components/Back";
import { Agent } from "../../../../../interfaces";
import FormattedDateCell from "../../../../../components/FormattedDateCell";
import http from "../../../../../utils/http-common";
import { useNotification } from "../../../../../context/NotificationContext";

// Relative "last seen" from updated_at (UTC). A recent heartbeat == online.
const lastSeen = (iso?: string | null): { label: string; online: boolean } => {
  if (!iso) return { label: "never", online: false };
  const norm = /[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + "Z";
  const sec = Math.max(0, (Date.now() - new Date(norm).getTime()) / 1000);
  const online = sec < 120;
  let label: string;
  if (sec < 60) label = "just now";
  else if (sec < 3600) label = `${Math.floor(sec / 60)}m ago`;
  else if (sec < 86400) label = `${Math.floor(sec / 3600)}h ago`;
  else label = `${Math.floor(sec / 86400)}d ago`;
  return { label, online };
};

const Kpi = ({ label, value, color }: { label: string; value: number; color: string }) => (
  <Paper
    variant="outlined"
    sx={{ px: 2, py: 1, borderRadius: 2, minWidth: 104, display: "flex", flexDirection: "column", gap: 0.25 }}
  >
    <Typography variant="h5" fontWeight={800} sx={{ color, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.3 }}>
      {label}
    </Typography>
  </Paper>
);

// Inline status + approval control. Pending agents get one-click Approve /
// Decline right in the row (Approve also enables the agent so it can pick up
// work immediately); approved/declined agents show a chip with the opposite
// quick action.
const StatusCell = ({
  agent,
  busy,
  onAction,
}: {
  agent: Agent;
  busy: boolean;
  onAction: (id: number, action: "approve" | "decline") => void;
}) => {
  const id = Number((agent as any).id);
  const status = String((agent as any).status || "");

  if (status === "pending") {
    return (
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Button
          size="small"
          variant="contained"
          color="success"
          startIcon={<CheckCircleIcon />}
          disabled={busy}
          onClick={() => onAction(id, "approve")}
        >
          Approve
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<BlockIcon />}
          disabled={busy}
          onClick={() => onAction(id, "decline")}
        >
          Decline
        </Button>
      </Stack>
    );
  }
  if (status === "approve") {
    return (
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Chip size="small" label="Approved" color="success" />
        <Tooltip title="Revoke (decline)">
          <span>
            <IconButton size="small" color="error" disabled={busy} onClick={() => onAction(id, "decline")}>
              <BlockIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    );
  }
  if (status === "decline") {
    return (
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Chip size="small" label="Declined" color="error" />
        <Tooltip title="Approve">
          <span>
            <IconButton size="small" color="success" disabled={busy} onClick={() => onAction(id, "approve")}>
              <CheckCircleIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    );
  }
  return <Chip size="small" label={status || "unknown"} />;
};

const Agents = () => {
  const [metaModal, setMetaModal] = useState(false);
  const { notify } = useNotification();

  const dispatch = useDispatch<AppDispatch>();
  const [data, setData] = useState<Agent[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [selectedMetadata, setSelectedMetadata] = useState<object>({});
  const [busyId, setBusyId] = useState<number | null>(null);
  const [rowCount] = useState(0);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "id", desc: true }]);
  const navigate = useNavigate();

  const handleMetaOpen = (metadata: object) => {
    setSelectedMetadata(metadata);
    setMetaModal(true);
  };

  const fetchData = useCallback(
    debounce(async () => {
      setIsLoading(true);
      setIsRefetching(true);
      try {
        const data = await dispatch(fetchAgents()).unwrap();
        setData(data);
      } catch (error) {
        setIsError(true);
        console.error("Error fetching agents:", error);
      } finally {
        setIsRefetching(false);
        setIsLoading(false);
      }
    }, 300),
    [pagination.pageIndex, pagination.pageSize, sorting, globalFilter, columnFilters, dispatch]
  );

  // Approve / decline via the dedicated endpoints. Approve also enables the
  // agent (state=true) so it is ready to run cases without a second step.
  const changeStatus = useCallback(
    async (id: number, action: "approve" | "decline") => {
      setBusyId(id);
      try {
        await http.post(`/agent/${id}/${action}`);
        notify(
          action === "approve" ? "Agent approved & enabled" : "Agent declined",
          action === "approve" ? "success" : "info"
        );
        const fresh = await dispatch(fetchAgents()).unwrap();
        setData(fresh);
      } catch (e) {
        console.error("Failed to change agent status:", e);
        notify("Failed to update agent status", "error");
      } finally {
        setBusyId(null);
      }
    },
    [dispatch, notify]
  );

  const handleEdit = (id: number) => navigate(`/management/agents/${id}/edit`);
  const handleView = (id: number) => navigate(`/management/agents/${id}/view`);
  const handleResources = (id: number) => navigate(`/management/agents/${id}/resources`);
  const handleCasesByAgent = (id: string) => navigate(`/management/agents/${id}/cases`);
  const handleProcedureByAgent = (id: string) => navigate(`/management/agents/${id}/procedures`);

  useEffect(() => {
    fetchData();
  }, [pagination.pageIndex, pagination.pageSize, sorting, columnFilters, globalFilter]);

  const stats = useMemo(() => {
    const total = data.length;
    const online = data.filter((a) => lastSeen((a as any).updated_at).online).length;
    const pending = data.filter((a) => String((a as any).status) === "pending").length;
    const approved = data.filter((a) => String((a as any).status) === "approve").length;
    return { total, online, pending, approved };
  }, [data]);

  const columns = useMemo<MRT_ColumnDef<Agent>[]>(
    () => [
      { accessorKey: "id", header: "ID", size: 70 },
      {
        accessorKey: "title",
        header: "Agent",
        size: 240,
        Cell: ({ row, cell }) => {
          const seen = lastSeen((row.original as any).updated_at);
          return (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Tooltip title={seen.online ? "Online (recent heartbeat)" : `Last seen ${seen.label}`}>
                <FiberManualRecordIcon sx={{ fontSize: 11, color: seen.online ? "#16a34a" : "#bdbdbd" }} />
              </Tooltip>
              <Tooltip title={cell.getValue<string>()}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {cell.getValue<string>()}
                </Typography>
              </Tooltip>
            </Stack>
          );
        },
      },
      {
        accessorKey: "state",
        header: "State",
        size: 110,
        Cell: ({ cell }) => (
          <Chip
            size="small"
            label={cell.getValue() ? "Active" : "Inactive"}
            color={cell.getValue() ? "success" : "default"}
            variant={cell.getValue() ? "filled" : "outlined"}
          />
        ),
      },
      {
        accessorKey: "status",
        header: "Status / Approval",
        size: 230,
        Cell: ({ row }) => (
          <StatusCell agent={row.original} busy={busyId === Number(row.id)} onAction={changeStatus} />
        ),
      },
      { accessorKey: "host", header: "Host", size: 140 },
      {
        accessorFn: (row: Agent) => row.meta_data?.version,
        header: "Version",
        size: 100,
        filterVariant: "select",
        Cell: ({ cell }) => {
          const version = cell.getValue<string>();
          return (
            <Chip size="small" variant="outlined" label={version || "N/A"} sx={{ fontWeight: 600 }} />
          );
        },
      },
      {
        accessorKey: "UUID",
        header: "UUID",
        size: 160,
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string>()}>
            <Typography
              variant="caption"
              sx={{ fontFamily: "monospace", color: "text.secondary", display: "block", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {cell.getValue<string>()}
            </Typography>
          </Tooltip>
        ),
      },
      { accessorKey: "os_type", header: "OS", size: 100 },
      {
        accessorKey: "updated_at",
        header: "Last seen",
        size: 150,
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
      {
        accessorKey: "meta_data",
        header: "Meta",
        size: 90,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => (
          <Button variant="outlined" size="small" onClick={() => handleMetaOpen(cell.getValue<any>())}>
            View
          </Button>
        ),
      },
    ],
    [busyId, changeStatus]
  );

  const table = useMaterialReactTable({
    columns,
    data,
    mrtTheme: (theme) => ({ baseBackgroundColor: theme.palette.background.paper }),
    muiTablePaperProps: {
      elevation: 0,
      sx: { borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" },
    },
    muiTableContainerProps: { sx: { maxHeight: "calc(78vh - 112px)" } },
    muiTableHeadCellProps: {
      sx: { fontWeight: 700, bgcolor: "background.default", color: "text.secondary", letterSpacing: 0.2 },
    },
    muiPaginationProps: {
      color: "secondary",
      rowsPerPageOptions: [10, 25, 50, 100],
      shape: "rounded",
      variant: "outlined",
    },
    paginationDisplayMode: "pages",
    columnFilterDisplayMode: "popover",
    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      density: "comfortable",
      showGlobalFilter: true,
      columnPinning: { right: ["mrt-row-actions"] },
      sorting: [{ id: "created_at", desc: false }],
    },
    enableColumnResizing: true,
    enableGrouping: false,
    enableRowSelection: false,
    enableRowActions: true,
    enableColumnOrdering: false,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    positionActionsColumn: "last",
    enableFacetedValues: true,
    getRowId: (row) => String(row.id),
    layoutMode: "grid-no-grow",
    muiToolbarAlertBannerProps: isError ? { color: "error", children: "Error loading data" } : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    rowCount,
    state: {
      isLoading,
      pagination,
      columnFilters,
      globalFilter,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },
    renderTopToolbarCustomActions: () => (
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Tooltip arrow title="Refresh Data">
          <IconButton onClick={() => fetchData()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        {stats.pending > 0 && (
          <Chip color="warning" label={`${stats.pending} pending approval`} icon={<CheckCircleIcon />} sx={{ fontWeight: 700 }} />
        )}
      </Stack>
    ),
    renderRowActionMenuItems: ({ closeMenu, row }) => [
      <MenuItem key={0} onClick={() => { handleEdit(Number(row.id)); closeMenu(); }} sx={{ m: 0 }}>Edit</MenuItem>,
      <MenuItem key={1} onClick={() => { handleView(Number(row.id)); closeMenu(); }} sx={{ m: 0 }}>View</MenuItem>,
      <MenuItem key={2} onClick={() => { handleResources(Number(row.id)); closeMenu(); }} sx={{ m: 0 }}>Resources</MenuItem>,
      <MenuItem key={3} onClick={() => { handleCasesByAgent(row.original.UUID); closeMenu(); }} sx={{ m: 0 }}>Cases</MenuItem>,
      <MenuItem key={4} onClick={() => { handleProcedureByAgent(row.original.UUID); closeMenu(); }} sx={{ m: 0 }}>Procedures</MenuItem>,
    ],
  });

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <MemoryIcon color="primary" />
        <Title>Agents Overview</Title>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Approve new agents, manage their state and inspect what each can run.
      </Typography>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }} flexWrap="wrap" useFlexGap>
        <Kpi label="AGENTS" value={stats.total} color="#111827" />
        <Kpi label="ONLINE" value={stats.online} color="#16a34a" />
        <Kpi label="PENDING" value={stats.pending} color="#d97706" />
        <Kpi label="APPROVED" value={stats.approved} color="#1565C0" />
      </Stack>

      <MaterialReactTable
        table={table}
        globalFilterFn={(row: MRT_Row<Agent>, columnIds: string[], filterValue: string): boolean => {
          const searchableColumns: string[] = ["id", "title", "host", "UUID", "version"];
          return columnIds
            .filter((columnId: string) => searchableColumns.includes(columnId))
            .some((columnId: string) => {
              const rowValue: unknown = row.getValue(columnId);
              return String(rowValue).toLowerCase().includes(filterValue.toLowerCase());
            });
        }}
      />

      <GlobalModal open={metaModal} onClose={() => setMetaModal(false)} title="Meta Data">
        <JsonEditor minWidth={"100%"} data={selectedMetadata} restrictAdd={true} restrictDelete={true} restrictEdit={true} />
      </GlobalModal>
      <Back />
    </>
  );
};

export default Agents;
